import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  BarController,
  LineController,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import './ViewData.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  BarController,
  LineController,
  Title,
  Tooltip,
  Legend
);

const gradeMap = [
  '4', '4+', '5', '5+',
  '6a', '6a+', '6b', '6b+', '6c', '6c+',
  '7a', '7a+', '7b', '7b+', '7c', '7c+',
  '8a', '8a+', '8b', '8b+', '8c', '8c+',
  '9a',
];

// Helper: parse grade string to index (1-based)
const parseDifficulty = (difficulty) => {
  return gradeMap.indexOf(String(difficulty || '').trim().toLowerCase()) + 1;
};
// Helper: get grade string from average index
const getClosestGrade = (avg) => {
  if (!avg || isNaN(avg)) return '';
  const idx = Math.round(avg) - 1;
  return gradeMap[idx] || '';
};

// Group completions by session (date)
function groupBySession(completions) {
  const sessions = {};
  completions.forEach(c => {
    const timestamp = Date.parse(c.date);
    if (Number.isNaN(timestamp)) return;
    const date = new Date(timestamp).toISOString().slice(0, 10);
    if (!sessions[date]) sessions[date] = [];
    sessions[date].push(c);
  });
  return sessions;
}

const ViewData = () => {
  const [tag, setTag] = useState('');
  const [filteredCompletions, setFilteredCompletions] = useState([]);
  const [progressData, setProgressData] = useState(null);
  const [sessions, setSessions] = useState({});
  const [sessionDates, setSessionDates] = useState([]);
  const [sessionHardest, setSessionHardest] = useState([]);
  const [sessionAverages, setSessionAverages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const requestIdRef = useRef(0);

  const fetchData = async () => {
    const normalizedTag = tag.trim().toUpperCase();
    if (!normalizedTag) {
      setErrorMessage('Please enter a valid tag.');
      return;
    }

    setLoading(true);
    const requestId = ++requestIdRef.current;

    try {
      const response = await axios.get(
        'https://bouldering-backend.vercel.app/api/completions'
      );
      const fetchedRoutes = Array.isArray(response.data) ? response.data : [];

      // Filter nested completions by tag
      const filtered = fetchedRoutes
        .filter((route) => route && typeof route === 'object')
        .flatMap((route) =>
          (Array.isArray(route.completions)
            ? route.completions.filter((completion) => completion && typeof completion === 'object')
          : [])
            .filter((completion) => completion.tag === normalizedTag)
            .map((completion) => ({
              routeName: route.name || 'Unnamed route',
              difficulty: route.difficulty || '',
              date: completion.date,
              votedGrade: completion.votedGrade,
            }))
        );

      // Only keep completions with a valid grade
      const validFiltered = filtered.filter(
        (completion) =>
          parseDifficulty(completion.difficulty) > 0 &&
          !Number.isNaN(Date.parse(completion.date))
      );

      if (requestId !== requestIdRef.current) return;

      if (filtered.length === 0) {
        setErrorMessage('No completions found for this tag.');
        setFilteredCompletions([]);
        setProgressData(null);
        setSessions({});
        setSessionDates([]);
        setSessionHardest([]);
        setSessionAverages([]);
        return;
      }

      if (validFiltered.length === 0) {
        setErrorMessage('No valid grades found for your completions.');
        setFilteredCompletions([]);
        setProgressData(null);
        setSessions({});
        setSessionDates([]);
        setSessionHardest([]);
        setSessionAverages([]);
        return;
      }

      if (requestId !== requestIdRef.current) return;
      setFilteredCompletions(validFiltered);
      setErrorMessage('');
      processSessionData(validFiltered);
    } catch (error) {
      if (requestId === requestIdRef.current) {
        setErrorMessage('Failed to fetch data. Please try again later.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  // Process sessions and chart data
  const processSessionData = (data) => {
    // Group by session (date)
    const allSessions = groupBySession(data);
    const allSessionDates = Object.keys(allSessions).sort();

    // Prepare filtered sessions and stats
    const filteredSessions = {};
    const filteredSessionDates = [];
    const sessionHardest = [];
    const sessionAverages = [];

    allSessionDates.forEach(date => {
      // Only keep completions with a valid grade
      const validCompletions = allSessions[date].filter(c => parseDifficulty(c.difficulty) > 0);
      if (validCompletions.length) {
        filteredSessions[date] = validCompletions;
        filteredSessionDates.push(date);
        const grades = validCompletions.map(c => parseDifficulty(c.difficulty));
        sessionHardest.push(Math.max(...grades));
        const avg = grades.reduce((a, b) => a + b, 0) / grades.length;
        sessionAverages.push(avg);
      }
    });

    setSessions(filteredSessions);
    setSessionDates(filteredSessionDates);
    setSessionHardest(sessionHardest);
    setSessionAverages(sessionAverages);

    setProgressData(
      filteredSessionDates.length > 0
        ? {
            labels: filteredSessionDates,
            datasets: [
              {
                label: 'Hardest Grade',
                data: sessionHardest,
                backgroundColor: 'rgba(30, 144, 255, 0.7)',
                borderRadius: 6,
                maxBarThickness: 32,
              },
              {
                label: 'Average Grade',
                data: sessionAverages,
                type: 'line',
                borderColor: 'rgba(255, 99, 132, 0.7)',
                borderWidth: 2,
                fill: false,
                pointRadius: 4,
                yAxisID: 'y',
              },
            ],
          }
        : null
    );
  };

  return (
    <div className="view-data-container">
      <h1>View Data</h1>
      <form
        className="search-bar"
        onSubmit={(event) => {
          event.preventDefault();
          fetchData();
        }}
      >
        <label htmlFor="tag-input" style={{ fontWeight: 500 }}>Tag:</label>
        <input
          id="tag-input"
          type="text"
          placeholder="Enter tag"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          autoComplete="off"
        />
        <button type="submit" disabled={loading}>
          {loading ? 'Loading...' : 'Search'}
        </button>
      </form>

      <div style={{ background: '#f7f9fa', borderRadius: 6, padding: '12px 16px', marginBottom: 18, color: '#333', fontSize: '1.02em' }}>
        <strong>Tip:</strong> Mark your completions for each session to track your progress. You can mark more than just your first completion in a session—add all routes you finish to get the most accurate session data!
      </div>

      {errorMessage && <p className="error-message" role="alert">{errorMessage}</p>}

      {!loading && !errorMessage && filteredCompletions.length === 0 && (
        <p style={{ color: '#888', marginTop: 24 }}>No sessions found for this tag.</p>
      )}

      {filteredCompletions.length > 0 && sessionDates.length > 0 && (
        <div className="data-display">
          <p style={{fontSize: '0.95em', color: '#666', marginBottom: 8}}>
            Each session groups all completions saved on the same day.
          </p>
          <h2>Sessions</h2>
          <ul className="session-list">
            {sessionDates.map(date => {
              const session = sessions[date] || [];
              const hardestIdx = sessionDates.indexOf(date);
              const hardest = sessionHardest[hardestIdx];
              const avg = sessionAverages[hardestIdx];
              return (
                <li key={date} className="session-summary">
                  <strong>{date}</strong>
                  &mdash; Routes: {session.length}
                  {hardest ? <> | Hardest: <span style={{color:'#1e90ff'}}>{gradeMap[hardest - 1]}</span></> : null}
                  {avg ? <> | Avg: <span style={{color:'#ff6384'}}>{getClosestGrade(avg)}</span></> : null}
                  <ul>
                    {session.map((c, i) => (
                      <li key={i} style={{marginLeft: 8}}>
                        {c.routeName} ({c.difficulty}) - {c.votedGrade}
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>

          <h2>Progress Chart</h2>
          {progressData && (
            <div className="chart-wrapper">
              <Bar
                data={progressData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { display: true },
                    title: { display: false },
                  },
                  scales: {
                    y: {
                      title: { display: true, text: 'Grade' },
                      ticks: {
                        callback: (value) => gradeMap[value - 1] || '',
                        stepSize: 1,
                        font: { size: 12 },
                      },
                      min: 1,
                      max: gradeMap.length,
                    },
                    x: {
                      title: { display: true, text: 'Session Date' },
                      ticks: {
                        font: { size: 10 },
                        maxRotation: 60,
                        minRotation: 30,
                        autoSkip: true,
                        maxTicksLimit: 6,
                      },
                    },
                  },
                  maintainAspectRatio: false,
                }}
                height={300}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ViewData;
